import React, { useEffect, useRef, useState } from 'react';
import { useTalent } from "../../../providers/TalentProvider";
import ProfileContainer from "../components/ProfileContainer";
import CircleProgress from "../../../components/CircleProgress";
import {
    EngagementFitBreakdown,
    RoleMatchBreakdown,
} from "./hire-confidence/HireConfidenceBreakdowns";
import { COMPONENT_NOTES, themeFor } from "./hire-confidence/HireConfidenceParts";

/**
 * How a talent's Hire Confidence score is built.
 *
 * Everything here serves one question: can a reviewer see the total, see the
 * four parts it came from, and drill into any one of them without losing the
 * shape of the whole. Hence four cards side by side that never move — the row
 * is the comparison — with a single panel beneath them.
 *
 * Only two of the four open. Role Match and Engagement Fit carry 90% of the
 * score between them and have real depth behind them; Market Demand and Prior
 * Outcomes are a badge and three counts each, and burying those behind a click
 * cost more than it saved, so they sit on the card as `HistoryCard`.
 *
 * Internal-only: gated to `canManageClients()` in TalentProfilePage while the
 * weights and scoring are being validated.
 */

const RING_PURPLE = 'var(--theme-primary, #5F3DB1)';

/** Must match the panel's CSS transition, or its contents clear mid-collapse. */
const PANEL_TRANSITION_MS = 350;

/**
 * The two components with more behind them than a card can hold, and so the two
 * that get a panel. The other two are a badge and three counts each; Market
 * Demand and Prior Outcomes render as `HistoryCard` instead — no colour, no
 * bar, and the badge rather than the score as the headline.
 */
const EXPANDABLE = ['roleMatch', 'engagementFit'];

/**
 * The expanded detail for a card. Both panels live in HireConfidenceBreakdowns
 * and are mounted from there rather than rebuilt here, so the candidate tile's
 * modals and this section can't drift apart on the same calculation.
 */
const ComponentBreakdown = ({ componentKey, talent, metrics, isOpen }) => {
    switch (componentKey) {
        case 'roleMatch':
            return (
                <RoleMatchBreakdown
                    talent={talent}
                    metrics={metrics}
                    isOpen={isOpen}
                    criteriaHref="#match-score"
                />
            );
        case 'engagementFit':
            return <EngagementFitBreakdown talent={talent} />;
        default:
            return null;
    }
};

/**
 * One figure in a badge's evidence line, dropped entirely at zero — "0 Rehires"
 * beside an Emerging Performer badge reads as a contradiction.
 *
 * The separator's spaces are written as an explicit string rather than left as
 * whitespace in the markup: JSX strips whitespace between elements on separate
 * lines, so the dot and the figures either side of it ended up as adjacent
 * `whitespace-nowrap` spans with no break opportunity anywhere between them.
 * The line then couldn't wrap at all, and ran off the edge of the card.
 */
const Stat = ({ count, noun, isFirst }) => {
    if (!count) {
        return null;
    }

    return (
        <>
            {!isFirst && <span className="text-[#C4C4CC]">{' · '}</span>}
            <span className="whitespace-nowrap">
                <b className="font-bold text-[#0E0E14]">{count}</b>
                {` ${TB.pluralize(noun, count)}`}
            </span>
        </>
    );
};

/**
 * What each of the two history cards leads with, and the figures behind it.
 *
 * `empty` states what hasn't happened yet, never what the talent isn't. These
 * two are earned through Wripple, so someone new to the platform has no badge
 * for reasons that say nothing about them — and a card reading "not in demand"
 * turns an absence of data into a verdict a reviewer will carry into the rest
 * of the profile.
 */
const HISTORY = {
    marketDemand: {
        icon: 'fa-bolt',
        empty: 'No demand signals yet',
        summary: talent => {
            const demand = talent.demandSummary?.();

            return {
                badge: demand?.badge,
                stats: [
                    { count: demand?.job_invites_count, noun: 'Invite' },
                    { count: demand?.job_likes_count, noun: 'Like' },
                ],
            };
        },
    },
    priorOutcomes: {
        icon: 'fa-trophy',
        empty: 'No Wripple history yet',
        summary: talent => {
            const performance = talent.performanceSummary?.();

            return {
                // A talent with completed work who hasn't cleared a threshold
                // still gets a name — an unlabelled row of counts reads as a bug.
                badge: performance ? (performance.badge || 'Emerging Performer') : null,
                stats: [
                    { count: performance?.completed_projects_count, noun: 'Project' },
                    { count: performance?.unique_clients_count, noun: 'Client' },
                    { count: performance?.rehires_count, noun: 'Rehire' },
                ],
            };
        },
    },
};

/**
 * Market Demand and Prior Outcomes.
 *
 * Led by the badge rather than the number, because the badge is the finding.
 * These two decide five points each, and a 61 rendered at the same size as a
 * Role Match of 72 invites a comparison between figures that aren't remotely
 * comparable. So the score drops below a rule, small, next to the share that
 * qualifies it — present for anyone checking the arithmetic, and out of the way
 * of anyone who isn't.
 */
const HistoryCard = ({ component, talent }) => {
    const config = HISTORY[component.key];
    const { badge, stats } = config.summary(talent);
    const hasScore = component.score !== null && component.score !== undefined;
    const possible = Math.round(component.weight * 100);

    // Broken deliberately rather than left to wrap: a line that wraps on its
    // own breaks after a separator, so the second line starts with a dangling
    // "·" indented past the first. Dropping the last figure to its own line
    // gives two lines flush to the same left edge instead.
    //
    // Only worth doing when there are three, though — two fit, and splitting
    // them buys the alignment fix for a problem that wasn't going to happen.
    const shown = TB.filter(stats, stat => !!stat.count);
    const splitAt = shown.length > 2 ? shown.length - 1 : shown.length;
    const head = shown.slice(0, splitAt);
    const tail = shown.slice(splitAt);

    return (
        // `min-w-0` because a grid item defaults to `min-width: auto` and so
        // refuses to shrink below its content — which is how a stats line ran
        // past the edge of its own card.
        <div className="relative flex min-w-0 flex-col items-stretch rounded-2xl border border-[#E2E3EA] bg-white p-4 text-left">
            <span className="block text-[9px] font-bold uppercase leading-none tracking-[0.09em] text-[#A2A6AF]">
                {component.label}
            </span>

            {/* Top-aligned, so a badge that runs to two lines keeps its glyph
                beside the first of them rather than floating to the middle. */}
            <span className="mt-2.5 flex flex-row items-start gap-2">
                {/* Dropped entirely when nothing has been earned. An empty
                    outlined disc is a placeholder for an award, and drawing the
                    shape of one next to "No Wripple history yet" makes the
                    absence look like a result. */}
                {badge && (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F1F1F4] text-[9px] text-[#4A4D57]">
                        <i className={`fas ${config.icon}`} />
                    </span>
                )}

                <span className="min-w-0 flex-1">
                    <span className={TB.mapClass('block text-xs font-bold leading-[1.35] tracking-[-0.01em]', {
                        'text-[#0E0E14]': !!badge,
                        'text-[#A2A6AF]': !badge,
                    })}>
                        {badge || config.empty}
                    </span>

                    {/* Indented with the badge, not with the card: the figures
                        are evidence for the badge above them, and starting them
                        back at the card's edge cut them loose from it. */}
                    {badge && shown.length > 0 && (
                        <span className="mt-1.5 block text-[11px] leading-[1.55] text-[#6C7280]">
                            <span className="block">
                                {TB.map(head, (stat, index) => (
                                    <Stat
                                        key={stat.noun}
                                        count={stat.count}
                                        noun={stat.noun}
                                        isFirst={index === 0}
                                    />
                                ))}
                            </span>

                            {tail.length > 0 && (
                                <span className="block">
                                    {TB.map(tail, (stat, index) => (
                                        <Stat
                                            key={stat.noun}
                                            count={stat.count}
                                            noun={stat.noun}
                                            isFirst={index === 0}
                                        />
                                    ))}
                                </span>
                            )}
                        </span>
                    )}
                </span>
            </span>

            {/* `mt-auto` on the rule, not the row below it: one margin utility
                per element, since two would tie under `important: true`. */}
            <span className="mt-auto block h-px bg-[#EDEEF2]" />

            <span className="mt-3 flex flex-row flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className={TB.mapClass('text-xl font-extrabold leading-none tabular-nums', {
                    'text-[#0E0E14]': hasScore,
                    'text-[#C4C4CC]': !hasScore,
                })}>
                    {hasScore ? Math.round(component.score) : '—'}
                </span>

                <span className="text-[9px] font-bold uppercase leading-none tracking-[0.06em] text-[#A2A6AF]">
                    {possible}% of total
                </span>
            </span>

            <span className="mt-2 block text-[11px] leading-[1.45] text-[#8B8F9A]">
                {COMPONENT_NOTES[component.key]}
            </span>
        </div>
    );
};

/**
 * Role Match and Engagement Fit: the two that carry 90% of the score between
 * them, and the two with a panel behind them.
 *
 * Selecting one never moves it. It only tints, borders it in its own colour and
 * drops a tail toward the panel — the row of cards is the comparison, and a
 * layout that rearranges itself every time you ask about one component makes
 * the others harder to hold in your head.
 */
const ScoreCard = ({ component, isOpen, onToggle }) => {
    const theme = themeFor(component.key);
    const hasScore = component.score !== null && component.score !== undefined;
    const possible = Math.round(component.weight * 100);

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            // `min-w-0` because a grid item defaults to `min-width: auto` and
            // so refuses to shrink below its content.
            className={TB.mapClass(
                'group relative flex min-w-0 flex-col items-stretch rounded-2xl border p-4 text-left transition-colors duration-200',
                {
                    [`${theme.border} ${theme.soft}`]: isOpen,
                    [`border-[#E2E3EA] bg-white ${theme.borderHover} ${theme.softHover}`]: !isOpen,
                }
            )}
        >
            {/* The name gets the full width of the card and wraps if it needs
                to. Nothing shares this row: a pill beside it was squeezing
                "Engagement Fit" down to "Engage…", and a component you can't
                read the name of is worse than a card a line taller. */}
            <span className="flex flex-row items-center gap-2">
                <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] text-white"
                    style={{ backgroundColor: theme.hex }}
                >
                    <i className={`fas ${theme.icon}`} />
                </span>
                <span className="text-sm font-bold leading-[1.3] tracking-[-0.01em] text-[#0E0E14]">
                    {component.label}
                </span>
            </span>

            {/* The score, and the share of the total it was scored against.
                A 61 means one thing at 45% and another at 5%. */}
            <span className="mt-3 flex flex-row items-end justify-between gap-2">
                <span className={TB.mapClass('text-[44px] font-extrabold leading-none tabular-nums tracking-[-0.03em]', {
                    'text-[#0E0E14]': hasScore,
                    'text-[#C4C4CC]': !hasScore,
                })}>
                    {hasScore ? Math.round(component.score) : '—'}
                </span>

                <span className="shrink-0 whitespace-nowrap pb-1 text-right text-[9px] font-bold uppercase leading-none tracking-[0.06em] text-[#A2A6AF]">
                    {possible}% of total
                </span>
            </span>

            <span className="mt-2.5 block h-1 w-full overflow-hidden rounded-full bg-[#EFEFF3]">
                <span
                    className="block h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{
                        width: `${hasScore ? Math.min(Math.max(component.score, 0), 100) : 0}%`,
                        backgroundColor: theme.hex,
                    }}
                />
            </span>

            <span className="mt-2.5 block text-[11px] leading-[1.45] text-[#8B8F9A]">
                {COMPONENT_NOTES[component.key]}
            </span>

            <span className={`mt-auto flex flex-row items-center gap-1.5 pt-3 text-[11px] font-bold ${theme.text}`}>
                {isOpen ? 'Hide breakdown' : 'See the breakdown'}
                <i className={TB.mapClass('fas fa-chevron-down text-[9px] transition-transform duration-300', {
                    'rotate-180': isOpen,
                })} />
            </span>

            {/* A tail into the panel below, so which card the breakdown belongs
                to is a matter of sight rather than of memory. Only drawn where
                all four cards share one row — wrapped, it would point at a
                sibling instead of at the panel. */}
            {isOpen && (
                <span
                    className={`absolute -bottom-[7px] left-1/2 hidden h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r xl:block ${theme.border} ${theme.soft}`}
                    aria-hidden="true"
                />
            )}
        </button>
    );
};

const TalentProfileHireConfidence = () => {
    const { talent, criteria } = useTalent();

    // Nothing open to start: the four cards are the summary, and leading with
    // one expanded hides the comparison the layout exists for.
    const [openKey, setOpenKey] = useState(null);

    // What the panel is currently rendering, which lags `openKey` on the way
    // down: clearing the content the moment it's dismissed would leave an empty
    // box to collapse, so the panel would blink out instead of closing.
    const [renderedKey, setRenderedKey] = useState(null);

    const clearTimer = useRef(null);

    /**
     * Opens, closes, or switches the panel.
     *
     * Switching between the two cards leaves it open and swaps the contents, so
     * it reads as moving between components rather than as closing one thing
     * and opening another.
     *
     * Nothing scrolls. The cards stay put and the panel grows beneath them, so
     * the page never moves under the reader and the row stays on screen.
     */
    const toggle = (key) => {
        const next = openKey === key ? null : key;

        setOpenKey(next);
        clearTimeout(clearTimer.current);

        if (next) {
            setRenderedKey(next);
            return;
        }

        clearTimer.current = setTimeout(() => setRenderedKey(null), PANEL_TRANSITION_MS);
    };

    useEffect(() => () => clearTimeout(clearTimer.current), []);

    const score = talent?.hireConfidenceScore?.();

    if (score === null || score === undefined) {
        return null;
    }

    const components = talent.hireConfidenceComponents?.() || [];

    // Lags `openKey` while the panel closes — see `renderedKey`.
    const renderedComponent = TB.find(components, ['key', renderedKey]);
    const panelTheme = renderedComponent ? themeFor(renderedComponent.key) : null;

    // The profile-side criteria breakdown, from the same calculation the Match
    // Score section uses. Guarded on `hasMatching()` because the helper reads
    // straight into `matching` and throws when a talent has none.
    const metrics = talent.hasMatching?.()
        ? (talent.matchScoresForCriteria?.(criteria) || {})
        : {};

    const earned = TB.sumBy(components, component => (component.score || 0) * component.weight);
    const unearned = TB.filter(components, component => component.score === null || component.score === undefined);

    return (
        <div id="hire-confidence" className="pt-2 pb-8">
            <ProfileContainer>

                {/* The score, and immediately the arithmetic behind it — the
                    cards below are the same sum, itemised. */}
                <div className="flex flex-col gap-5 border-b border-[#EDEEF2] pb-6 sm:flex-row sm:items-center sm:gap-6">
                    <div className="h-[120px] w-[120px] shrink-0">
                        <CircleProgress
                            progress={talent.hireConfidenceScore(false)}
                            strokeWidth={8}
                            pathColor={RING_PURPLE}
                            trailColor="#EFEFF3"
                        >
                            <p className="m-0">
                                <span className="text-3xl">{score}</span>
                            </p>
                        </CircleProgress>
                    </div>

                    <div className="min-w-0">
                        <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
                            <p className="mb-0 text-3xl font-bold">Hire Confidence</p>
                            <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.09em] text-white">
                                Beta
                            </span>
                        </div>

                        <p className="mb-0 mt-1 text-lg font-semibold text-primary">
                            {talent.hireConfidenceLabel?.()}
                        </p>

                        <p className="mb-0 mt-2 text-sm leading-[1.5] text-[#595D68]">
                            <strong className="tabular-nums text-[#0E0E14]">{TB.round(earned, 1)}</strong>
                            {' points earned of 100 available, across the four components below. '}
                            {unearned.length > 0 && (
                                <>
                                    {`${TB.pluralize('component', unearned.length, true)} earned nothing, capping this score at `}
                                    <strong className="tabular-nums text-[#0E0E14]">
                                        {100 - Math.round(TB.sumBy(unearned, component => component.weight * 100))}
                                    </strong>.
                                </>
                            )}
                        </p>
                    </div>
                </div>

                {/* Equal columns, fixed. The components side by side is the
                    comparison the section is for, and nothing below them is
                    allowed to disturb it. */}
                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {TB.map(components, component => (
                        TB.includes(EXPANDABLE, component.key) ? (
                            <ScoreCard
                                key={component.key}
                                component={component}
                                isOpen={openKey === component.key}
                                onToggle={() => toggle(component.key)}
                            />
                        ) : (
                            <HistoryCard
                                key={component.key}
                                component={component}
                                talent={talent}
                            />
                        )
                    ))}
                </div>

                {/* One panel, below all four, animated with grid rows rather
                    than by mounting: a height transition needs both states in
                    the DOM, and it lets the panel close smoothly instead of
                    vanishing. */}
                <div className={TB.mapClass('grid transition-all duration-[350ms] ease-out', {
                    'grid-rows-[1fr] opacity-100': !!openKey,
                    'grid-rows-[0fr] opacity-0': !openKey,
                })}>
                    <div className="overflow-hidden">
                        {/* No header of its own: the card above already names
                            the component and now carries its colour and its
                            tail, so repeating the title here would introduce a
                            second heading for one thing. Closing is the card
                            too — the control you opened it with. */}
                        {renderedComponent && (
                            <div className={`mt-3 rounded-2xl border bg-white p-5 ${panelTheme.border}`}>
                                <ComponentBreakdown
                                    componentKey={renderedComponent.key}
                                    talent={talent}
                                    metrics={metrics}
                                    isOpen={openKey === renderedComponent.key}
                                />
                            </div>
                        )}
                    </div>
                </div>

            </ProfileContainer>
        </div>
    );
};

export default TalentProfileHireConfidence;
